import {
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  ColorManagement,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  Texture,
  Vector3,
} from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

import { AXES_DEFAULTS, COLOR_CODES, CUBE_DEFAULTS } from '../constants';

type BaseFace = {
  name: string;
  coords: [number, number, number];
  rotate: ('X' | 'Y' | 'Z')[];
};

export type UserData = {
  name: string;
  position: Vector3;
  up: Vector3[];
  coords: [number, number, number];
};

type HasUserData = {
  userData: Pick<UserData, 'position' | 'up'>;
};

export type CubeMesh = Mesh<BufferGeometry, MeshStandardMaterial> & HasUserData;

//IMPORTANT: ColorManagement must be disabled before THREE.Color is used (even indirectly when creating MeshStandardMaterial)
//See https://discourse.threejs.org/t/updates-to-color-management-in-three-js-r152/50791
ColorManagement.enabled = false;

const TURN_RADIANS = Math.PI / 2;

const makeFaceLabelTexture = (label: string): Texture => {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  const fontsize = 112;
  const fontface = 'Libre Franklin';
  context.font = fontsize + 'px ' + fontface;
  const metrics = context.measureText(label);
  const textWidth = metrics.width;
  context.fillStyle = '#000000';
  context.fillText(
    label,
    (canvas.width - textWidth) / 2,
    canvas.height - (canvas.height - fontsize) / 2,
  );

  const texture = new CanvasTexture(canvas);
  texture.needsUpdate = true;

  return texture;
};

export const makeCubeGeometry = (group: Group, selectables: CubeMesh[]) => {
  const l = CUBE_DEFAULTS.CUBE_LENGTH * 0.5;
  const e = CUBE_DEFAULTS.CUBE_EDGE;
  const t = CUBE_DEFAULTS.CUBE_THICKNESS;

  // prettier-ignore
  const baseFaces: BaseFace[] = [
    {
      name: 'Front',
      coords: [1, 0, 0],
      rotate: ['Z', 'Y']
    },
    {
      name: 'Right',
      coords: [0, 1, 0],
      rotate: ['X', 'Z', 'Z']
    },
    {
      name: 'Top',
      coords: [0, 0, 1],
      rotate: ['Z']
    },
    {
      name: 'Back',
      coords: [-1, 0, 0],
      rotate: ['Y', 'X', 'Z', 'Z']
    },
    {
      name: 'Left',
      coords: [0, -1, 0],
      rotate: ['X']
    },
    {
      name: 'Bottom',
      coords: [0, 0, -1],
      rotate: ['Y', 'Y', 'Z', 'Z', 'Z']
    },
  ];

  for (const face of baseFaces) {
    const geom = new PlaneGeometry(2 * l, 2 * l);

    for (const axis of face.rotate) {
      if (axis === 'X') {
        geom.rotateX(TURN_RADIANS);
      }
      if (axis === 'Y') {
        geom.rotateY(TURN_RADIANS);
      }
      if (axis === 'Z') {
        geom.rotateZ(TURN_RADIANS);
      }
    }

    geom.translate(face.coords[0] * l, face.coords[1] * l, face.coords[2] * l);

    const baseMesh = new Mesh(
      geom,
      new MeshBasicMaterial({
        opacity: CUBE_DEFAULTS.CUBE_OPACITY,
        transparent: true,
      }),
    );
    const labelMesh = new Mesh(
      geom,
      new MeshBasicMaterial({
        map: makeFaceLabelTexture(face.name),
        transparent: true,
        depthTest: false,
      }),
    );
    group.add(baseMesh);
    group.add(labelMesh);
  }

  // Define base cube corners, base edges, and colored axes
  const c1 = new Vector3(l, l, l);
  const c2 = new Vector3(l, -l, l);
  const c3 = new Vector3(-l, -l, l);
  const c4 = new Vector3(-l, l, l);
  const c5 = new Vector3(l, l, -l);
  const c6 = new Vector3(l, -l, -l);
  const c7 = new Vector3(-l, -l, -l);
  const c8 = new Vector3(-l, l, -l);

  const a3 = new Vector3(-l, -l, l + CUBE_DEFAULTS.CUBE_AXES_OVERHANG);
  const a6 = new Vector3(l + CUBE_DEFAULTS.CUBE_AXES_OVERHANG, -l, -l);
  const a8 = new Vector3(-l, l + CUBE_DEFAULTS.CUBE_AXES_OVERHANG, -l);

  // prettier-ignore
  const edgeList = [
    [c2, c1], [c2, c3], [c2, c6], // pxnypz
    [c4, c1], [c4, c3], [c4, c8], // nxpypz
    [c5, c6], [c5, c8], [c5,c1],  // pxpynz
  ]

  const axesList = [
    { color: COLOR_CODES.X, coords: [c7, a6] },
    { color: COLOR_CODES.Y, coords: [c7, a8] },
    { color: COLOR_CODES.Z, coords: [c7, a3] },
  ];

  for (const edge of edgeList) {
    const g = new LineGeometry();
    g.setPositions([
      edge[0].x,
      edge[0].y,
      edge[0].z,
      edge[1].x,
      edge[1].y,
      edge[1].z,
    ]);
    const m = new LineMaterial({
      color: COLOR_CODES.CUBE_BORDER_COLOR,
      linewidth: 1,
    });
    m.resolution.set(AXES_DEFAULTS.AXES_SIZE, AXES_DEFAULTS.AXES_SIZE);
    const e = new Line2(g, m);
    group.add(e);
  }
  for (const axes of axesList) {
    const g = new LineGeometry();
    g.setPositions([
      axes.coords[0].x,
      axes.coords[0].y,
      axes.coords[0].z,
      axes.coords[1].x,
      axes.coords[1].y,
      axes.coords[1].z,
    ]);
    const m = new LineMaterial({
      color: axes.color,
      linewidth: 3,
    });
    m.resolution.set(AXES_DEFAULTS.AXES_SIZE, AXES_DEFAULTS.AXES_SIZE);
    const e = new Line2(g, m);
    group.add(e);
  }

  const pxPosition = new Vector3(1, 0, 0);
  const pyPosition = new Vector3(0, 1, 0);
  const pzPosition = new Vector3(0, 0, 1);
  const nxPosition = pxPosition.clone().multiplyScalar(-1);
  const nyPosition = pyPosition.clone().multiplyScalar(-1);
  const nzPosition = pzPosition.clone().multiplyScalar(-1);

  // Notes:
  // - See https://paperlessparts.atlassian.net/wiki/spaces/ENGINEERIN/pages/314277993/Viewer+Cube+Logic
  //   for details about faces, edges and corners
  // - About naming conventions: `p` prefix means positive, `n` prefix means negative
  //   So `px` means the face where normal is along the positive x axis, and `nx` is the face with normal along
  //   the negative x axis
  // prettier-ignore
  const faces: UserData[] = [
    {
      name: 'px',
      position: pxPosition,
      //4 vector choices: The face can be aligned upward based on each of its neighboring faces
      up: [pzPosition.clone(), pyPosition.clone(), nzPosition.clone(), nyPosition.clone()],
      coords: pxPosition.toArray()
    },
    {
      name: 'py',
      position: pyPosition,
      //4 vector choices: The face can be aligned upward based on each of its neighboring faces
      up: [pzPosition.clone(), nxPosition.clone(), nzPosition.clone(), pxPosition.clone()],
      coords: pyPosition.toArray()
    },
    {
      name: 'pz',
      position: pzPosition,
      //4 vector choices: The face can be aligned upward based on each of its neighboring faces
      up: [nyPosition.clone(), pxPosition.clone(), pyPosition.clone(), nxPosition.clone()],
      coords: pzPosition.toArray()
    },
    {
      name: 'nx',
      position: nxPosition,
      //4 vector choices: The face can be aligned upward based on each of its neighboring faces
      up: [pzPosition.clone(), nyPosition.clone(), nzPosition.clone(), pyPosition.clone()],
      coords: nxPosition.toArray()
    },
    {
      name: 'ny',
      position: nyPosition,
      //4 vector choices: The face can be aligned upward based on each of its neighboring faces
      up: [pzPosition.clone(), pxPosition.clone(), nzPosition.clone(), nxPosition.clone()],
      coords: nyPosition.toArray()
    },
    {
      name: 'nz',
      position: nzPosition,
      //4 vector choices: The face can be aligned upward based on each of its neighboring faces
      up: [nyPosition.clone(), nxPosition.clone(), pyPosition.clone(), pxPosition.clone()],
      coords: nzPosition.toArray()
    },
  ];

  // assertions for debugging during development: (currently no issues with the above definitions)
  // let faceHasError = false;
  // for (const [index, corner] of faces.entries()) {
  //   const {position, up} = corner;
  //   for (const [upIndex, _up] of up.entries()) {
  //     if (position.dot(_up) !== 0) {
  //       console.log(`face ${index} has up ${upIndex} that is not normal to position`);
  //       faceHasError = true;
  //     }
  //   }
  // }
  // if (faceHasError) {
  //   debugger;
  // }

  // prettier-ignore
  for (const face of faces) {
    const v1x = face.coords[0] ? (l + t) * face.coords[0] : l - e;
    const v1y = face.coords[1] ? (l + t) * face.coords[1] : l - e;
    const v1z = face.coords[2] ? (l + t) * face.coords[2] : l - e;
    const v1 = [v1x, v1y, v1z];

    const v2x = face.coords[0] ? (l + t) * face.coords[0] : face.coords[2] ? l - e : -l + e;
    const v2y = face.coords[1] ? (l + t) * face.coords[1] : face.coords[0] ? l - e : -l + e;
    const v2z = face.coords[2] ? (l + t) * face.coords[2] : face.coords[1] ? l - e : -l + e;
    const v2 = [v2x, v2y, v2z];

    const v3x = face.coords[0] ? (l + t) * face.coords[0] : -l + e;
    const v3y = face.coords[1] ? (l + t) * face.coords[1] : -l + e;
    const v3z = face.coords[2] ? (l + t) * face.coords[2] : -l + e;
    const v3 = [v3x, v3y, v3z];

    const v4x = face.coords[0] ? (l + t) * face.coords[0] : face.coords[1] ? l - e : -l + e;
    const v4y = face.coords[1] ? (l + t) * face.coords[1] : face.coords[2] ? l - e : -l + e;
    const v4z = face.coords[2] ? (l + t) * face.coords[2] : face.coords[0] ? l - e : -l + e;
    const v4 = [v4x, v4y, v4z];

    const v5x = face.coords[0] ? (l) * face.coords[0] : l - e;
    const v5y = face.coords[1] ? (l) * face.coords[1] : l - e;
    const v5z = face.coords[2] ? (l) * face.coords[2] : l - e;
    const v5 = [v5x, v5y, v5z];

    const v6x = face.coords[0] ? (l) * face.coords[0] : face.coords[2] ? l - e : -l + e;
    const v6y = face.coords[1] ? (l) * face.coords[1] : face.coords[0] ? l - e : -l + e;
    const v6z = face.coords[2] ? (l) * face.coords[2] : face.coords[1] ? l - e : -l + e;
    const v6 = [v6x, v6y, v6z];

    const v7x = face.coords[0] ? (l) * face.coords[0] : -l + e;
    const v7y = face.coords[1] ? (l) * face.coords[1] : -l + e;
    const v7z = face.coords[2] ? (l) * face.coords[2] : -l + e;
    const v7 = [v7x, v7y, v7z];

    const v8x = face.coords[0] ? (l) * face.coords[0] : face.coords[1] ? l - e : -l + e;
    const v8y = face.coords[1] ? (l) * face.coords[1] : face.coords[2] ? l - e : -l + e;
    const v8z = face.coords[2] ? (l) * face.coords[2] : face.coords[0] ? l - e : -l + e;
    const v8 = [v8x, v8y, v8z];

    const reverse = face.coords.filter(n => n !== 0).reduce((a, b) => a * b);

    let vert = [
      [v1, v5, v6, v6, v2, v1], // top edge
      [v6, v7, v3, v3, v2, v6], // left edge
      [v3, v7, v8, v8, v4, v3], // bottom edge
      [v8, v5, v1, v1, v4, v8], // right edge
      [v1, v2, v3, v3, v4, v1] // face
    ]

    if (reverse === 1) {
      vert = vert.map(vertFace => vertFace.reverse())
    }

    const geom = new BufferGeometry();
    geom.setAttribute(
      'position',
      new BufferAttribute(new Float32Array(vert.flat(2)), 3),
    );
    geom.computeVertexNormals();

    //face
    const mesh = new Mesh(
      geom,
      new MeshStandardMaterial({
        color: COLOR_CODES.CUBE_HOVER_COLOR,
        depthWrite: false,
        transparent: true,
        opacity: 0
      }),
    ) as CubeMesh;

    mesh.userData.position = face.position;
    mesh.userData.up = face.up;
    group.add(mesh);
    selectables.push(mesh);
  }

  const pxpyVector = new Vector3(1, 1, 0).normalize();
  const nxpyVector = new Vector3(-1, 1, 0).normalize();
  const nxnyVector = new Vector3(-1, -1, 0).normalize();
  const pxnyVector = new Vector3(1, -1, 0).normalize();
  const pxpzVector = new Vector3(1, 0, 1).normalize();
  const pypzVector = new Vector3(0, 1, 1).normalize();
  const nxpzVector = new Vector3(-1, 0, 1).normalize();
  const nypzVector = new Vector3(0, -1, 1).normalize();
  const pxnzVector = new Vector3(1, 0, -1).normalize();
  const pynzVector = new Vector3(0, 1, -1).normalize();
  const nxnzVector = new Vector3(-1, 0, -1).normalize();
  const nynzVector = new Vector3(0, -1, -1).normalize();

  // Notes:
  // - See https://paperlessparts.atlassian.net/wiki/spaces/ENGINEERIN/pages/314277993/Viewer+Cube+Logic
  //   for details about faces, edges and corners
  // - About naming conventions: `p` prefix means positive, `n` prefix means negative
  //   So `pxpy` means the edge between the px and py face. And `nxpy` is the edge between the nx and py face
  // prettier-ignore
  const edges: UserData[] = [
    {
      name: 'pxpy',
      position: pxpyVector,
      //4 vector choices: The edge can be aligned horizontally 2 ways, and vertically 2 ways
      //Note: candidates are normalized below
      up: [pzPosition.clone(), nxpyVector.clone(), nzPosition.clone(), pxnyVector.clone()],
      coords: [1, 1, 0]
    },
    {
      name: 'nxpy',
      position: nxpyVector,
      //4 vector choices: The edge can be aligned horizontally 2 ways, and vertically 2 ways
      //Note: candidates are normalized below
      up: [pzPosition.clone(), nxnyVector.clone(), nzPosition.clone(), pxpyVector.clone()],
      coords: [-1, 1, 0]
    },
    {
      name: 'nxny',
      position: nxnyVector,
      //4 vector choices: The edge can be aligned horizontally 2 ways, and vertically 2 ways
      //Note: candidates are normalized below
      up: [pzPosition.clone(), pxnyVector.clone(), nzPosition.clone(), nxpyVector.clone()],
      coords: [-1, -1, 0]
    },
    {
      name: 'pxny',
      position: pxnyVector,
      //4 vector choices: The edge can be aligned horizontally 2 ways, and vertically 2 ways
      //Note: candidates are normalized below
      up: [pzPosition.clone(), pxpyVector.clone(), nzPosition.clone(), nxnyVector.clone()],
      coords: [1, -1, 0]
    },
    {
      name: 'pxpz',
      position: pxpzVector,
      //4 vector choices: The edge can be aligned horizontally 2 ways, and vertically 2 ways
      //Note: candidates are normalized below
      up: [nxpzVector.clone(), pyPosition.clone(), pxnzVector.clone(), nyPosition.clone()],
      coords: [1, 0, 1]
    },
    {
      name: 'pypz',
      position: pypzVector,
      //4 vector choices: The edge can be aligned horizontally 2 ways, and vertically 2 ways
      //Note: candidates are normalized below
      up: [nypzVector.clone(), nxPosition.clone(), pynzVector.clone(), pxPosition.clone()],
      coords: [0, 1, 1]
    },
    {
      name: 'nxpz',
      position: nxpzVector,
      //4 vector choices: The edge can be aligned horizontally 2 ways, and vertically 2 ways
      //Note: candidates are normalized below
      up: [pxpzVector.clone(), nyPosition.clone(), nxnzVector.clone(), pyPosition.clone()],
      coords: [-1, 0, 1]
    },
    {
      name: 'nypz',
      position: nypzVector,
      //4 vector choices: The edge can be aligned horizontally 2 ways, and vertically 2 ways
      //Note: candidates are normalized below
      up: [pypzVector.clone(), pxPosition.clone(), nynzVector.clone(), nxPosition.clone()],
      coords: [0, -1, 1]
    },
    {
      name: 'pxnz',
      position: pxnzVector,
      //4 vector choices: The edge can be aligned horizontally 2 ways, and vertically 2 ways
      //Note: candidates are normalized below
      up: [pxpzVector.clone(), pyPosition.clone(), nxnzVector.clone(), nyPosition.clone()],
      coords: [1, 0, -1]
    },
    {
      name: 'pynz',
      position: pynzVector,
      //4 vector choices: The edge can be aligned horizontally 2 ways, and vertically 2 ways
      //Note: candidates are normalized below
      up: [pypzVector.clone(), nxPosition.clone(), nynzVector.clone(), pxPosition.clone()],
      coords: [0, 1, -1]
    },
    {
      name: 'nxnz',
      position: nxnzVector,
      //4 vector choices: The edge can be aligned horizontally 2 ways, and vertically 2 ways
      //Note: candidates are normalized below
      up: [nxpzVector.clone(), nyPosition.clone(), pxnzVector.clone(), pyPosition.clone()],
      coords: [-1, 0, -1]
    },
    {
      name: 'nynz',
      position: nynzVector,
      //4 vector choices: The edge can be aligned horizontally 2 ways, and vertically 2 ways
      //Note: candidates are normalized below
      up: [nypzVector.clone(), pxPosition.clone(), pynzVector.clone(), nxPosition.clone()],
      coords: [0, -1, -1]
    },
  ];

  //normalize the positions and up candidates
  for (const edge of edges) {
    edge.up.forEach((u) => u.normalize());
  }

  //Create position vectors in opposite direction for use in calculating edge up directions
  const pypxVector = pxpyVector.clone().multiplyScalar(-1);
  const pynxVector = nxpyVector.clone().multiplyScalar(-1);
  const nynxVector = nxnyVector.clone().multiplyScalar(-1);
  const nypxVector = pxnyVector.clone().multiplyScalar(-1);
  const pzpxVector = pxpzVector.clone().multiplyScalar(-1);
  const pzpyVector = pypzVector.clone().multiplyScalar(-1);
  const pznxVector = nxpzVector.clone().multiplyScalar(-1);
  const pznyVector = nypzVector.clone().multiplyScalar(-1);
  const nzpxVector = pxnzVector.clone().multiplyScalar(-1);
  const nzpyVector = pynzVector.clone().multiplyScalar(-1);
  const nznxVector = nxnzVector.clone().multiplyScalar(-1);
  const nznyVector = nynzVector.clone().multiplyScalar(-1);

  // assertions for debugging during development: (currently no issues with the above definitions)
  // let edgeHasError = false;
  // for (const [index, corner] of edges.entries()) {
  //   const {position, up} = corner;
  //   for (const [upIndex, _up] of up.entries()) {
  //     if (position.dot(_up) !== 0) {
  //       console.log(`edge ${index} has up ${upIndex} that is not normal to position`);
  //       edgeHasError = true;
  //     }
  //   }
  // }
  // if (edgeHasError) {
  //   debugger;
  // }

  // prettier-ignore
  for (const edge of edges) {
    const v1x = edge.coords[0] ? l * edge.coords[0] : l - e;
    const v1y = edge.coords[1] ? l * edge.coords[1] : l - e;
    const v1z = edge.coords[2] ? l * edge.coords[2] : l - e;
    const v1 = [v1x, v1y, v1z]; // top center face

    const v2x = edge.coords[0] ? (l + t) * edge.coords[0] : l - e;
    const v2y = edge.coords[1] ? (l + t) * edge.coords[1] : l - e;
    const v2z = edge.coords[2] ? (l + t) * edge.coords[2] : l - e;
    const v2 = [v2x, v2y, v2z];

    const v3x = edge.coords[0] === 0 ? l - e : edge.coords[1] === 0 ? (l - e) * edge.coords[0] : l * edge.coords[0];
    const v3y = edge.coords[1] === 0 ? l - e : edge.coords[2] === 0 ? (l - e) * edge.coords[1] : l * edge.coords[1];
    const v3z = edge.coords[2] === 0 ? l - e : edge.coords[0] === 0 ? (l - e) * edge.coords[2] : l * edge.coords[2];
    const v3 = [v3x, v3y, v3z];

    const v4x = edge.coords[0] === 0 ? l - e : edge.coords[1] === 0 ? (l - e) * edge.coords[0] : (l + t) * edge.coords[0];
    const v4y = edge.coords[1] === 0 ? l - e : edge.coords[2] === 0 ? (l - e) * edge.coords[1] : (l + t) * edge.coords[1];
    const v4z = edge.coords[2] === 0 ? l - e : edge.coords[0] === 0 ? (l - e) * edge.coords[2] : (l + t) * edge.coords[2];
    const v4 = [v4x, v4y, v4z];

    const v5x = edge.coords[0] === 0 ? l - e : edge.coords[2] === 0 ? (l - e) * edge.coords[0] : l * edge.coords[0];
    const v5y = edge.coords[1] === 0 ? l - e : edge.coords[0] === 0 ? (l - e) * edge.coords[1] : l * edge.coords[1];
    const v5z = edge.coords[2] === 0 ? l - e : edge.coords[1] === 0 ? (l - e) * edge.coords[2] : l * edge.coords[2];
    const v5 = [v5x, v5y, v5z];

    const v6x = edge.coords[0] === 0 ? l - e : edge.coords[2] === 0 ? (l - e) * edge.coords[0] : (l + t) * edge.coords[0];
    const v6y = edge.coords[1] === 0 ? l - e : edge.coords[0] === 0 ? (l - e) * edge.coords[1] : (l + t) * edge.coords[1];
    const v6z = edge.coords[2] === 0 ? l - e : edge.coords[1] === 0 ? (l - e) * edge.coords[2] : (l + t) * edge.coords[2];
    const v6 = [v6x, v6y, v6z];

    const v7x = edge.coords[0] ? l * edge.coords[0] : -l + e;
    const v7y = edge.coords[1] ? l * edge.coords[1] : -l + e;
    const v7z = edge.coords[2] ? l * edge.coords[2] : -l + e;
    const v7 = [v7x, v7y, v7z]; // top center face

    const v8x = edge.coords[0] ? (l + t) * edge.coords[0] : -l + e;
    const v8y = edge.coords[1] ? (l + t) * edge.coords[1] : -l + e;
    const v8z = edge.coords[2] ? (l + t) * edge.coords[2] : -l + e;
    const v8 = [v8x, v8y, v8z];

    const v9x = edge.coords[0] === 0 ? -l + e : edge.coords[1] === 0 ? (l - e) * edge.coords[0] : l * edge.coords[0];
    const v9y = edge.coords[1] === 0 ? -l + e : edge.coords[2] === 0 ? (l - e) * edge.coords[1] : l * edge.coords[1];
    const v9z = edge.coords[2] === 0 ? -l + e : edge.coords[0] === 0 ? (l - e) * edge.coords[2] : l * edge.coords[2];
    const v9 = [v9x, v9y, v9z];

    const v10x = edge.coords[0] === 0 ? -l + e : edge.coords[1] === 0 ? (l - e) * edge.coords[0] : (l + t) * edge.coords[0];
    const v10y = edge.coords[1] === 0 ? -l + e : edge.coords[2] === 0 ? (l - e) * edge.coords[1] : (l + t) * edge.coords[1];
    const v10z = edge.coords[2] === 0 ? -l + e : edge.coords[0] === 0 ? (l - e) * edge.coords[2] : (l + t) * edge.coords[2];
    const v10 = [v10x, v10y, v10z];

    const v11x = edge.coords[0] === 0 ? -l + e : edge.coords[2] === 0 ? (l - e) * edge.coords[0] : l * edge.coords[0];
    const v11y = edge.coords[1] === 0 ? -l + e : edge.coords[0] === 0 ? (l - e) * edge.coords[1] : l * edge.coords[1];
    const v11z = edge.coords[2] === 0 ? -l + e : edge.coords[1] === 0 ? (l - e) * edge.coords[2] : l * edge.coords[2];
    const v11 = [v11x, v11y, v11z];

    const v12x = edge.coords[0] === 0 ? -l + e : edge.coords[2] === 0 ? (l - e) * edge.coords[0] : (l + t) * edge.coords[0];
    const v12y = edge.coords[1] === 0 ? -l + e : edge.coords[0] === 0 ? (l - e) * edge.coords[1] : (l + t) * edge.coords[1];
    const v12z = edge.coords[2] === 0 ? -l + e : edge.coords[1] === 0 ? (l - e) * edge.coords[2] : (l + t) * edge.coords[2];
    const v12 = [v12x, v12y, v12z];

    const reverse = edge.coords.filter(n => n !== 0).reduce((a,b) => a * b);

    let vert = [
      [v1, v3, v4, v4, v2, v1, v1, v2, v6, v6, v5, v1], // top edges
      [v3, v9, v10, v10, v4, v3], // left edge
      [v5, v6, v12, v12, v11, v5], // right edge
      [v7, v8, v10, v10, v9, v7, v7, v11, v12, v12, v8, v7], // bottom edges
      [v2, v4, v10, v10, v8, v2, v2, v8, v12, v12, v6, v2]] // front faces

    if (reverse === -1) {
      vert = vert.map(vertFace => vertFace.reverse())
    }

    const geom = new BufferGeometry();
    geom.setAttribute(
      'position',
      new BufferAttribute(new Float32Array(vert.flat(2)), 3),
    );
    geom.computeVertexNormals();

    //edge
    const mesh = new Mesh(
      geom,
      new MeshStandardMaterial({
        color: COLOR_CODES.CUBE_HOVER_COLOR,
        depthWrite: false,
        transparent: true,
        opacity: 0
      }),
    ) as CubeMesh;

    mesh.userData.position = edge.position;
    mesh.userData.up = edge.up;
    group.add(mesh);
    selectables.push(mesh);
  }

  //About calcup():
  //- See whiteboard sketches on https://paperlessparts.atlassian.net/wiki/spaces/ENGINEERIN/pages/314277993/Viewer+Cube+Logic
  //- A candidate up vector is the projection of one of the corner's incoming edges onto the plane that has a normal in the direction
  //  of the camera position. (ie a plane tangent to the sphere at the camera position.)
  //- The edge is not normal to the position vector, so it must be projected. We can project the edge onto the position vector and this
  //  gives the component of the edge parallel to the position vector. But we want the component perpendicular to the position vector;
  //  which can be calculated by subtracting the projectedVector from the edgeDirVector.
  const calcUp = (edgeDir: Vector3, position: Vector3): Vector3 => {
    const normalizedEdgeDir = edgeDir.clone(); //already normalized
    const normalizedPosition = position.clone(); //already normalized
    const edgeDirProjectedOntoPosition = normalizedPosition
      .clone()
      .multiplyScalar(normalizedEdgeDir.dot(normalizedPosition));
    const up = normalizedEdgeDir
      .clone()
      .sub(edgeDirProjectedOntoPosition)
      .normalize();
    return up;
  };

  const pxpypzPosition = new Vector3(1, 1, 1).normalize();
  const pxpynzPosition = new Vector3(1, 1, -1).normalize();
  const pxnynzPosition = new Vector3(1, -1, -1).normalize();
  const pxnypzPosition = new Vector3(1, -1, 1).normalize();
  const nxnypzPosition = new Vector3(-1, -1, 1).normalize();
  const nxnynzPosition = new Vector3(-1, -1, -1).normalize();
  const nxpynzPosition = new Vector3(-1, 1, -1).normalize();
  const nxpypzPosition = new Vector3(-1, 1, 1).normalize();

  // Notes:
  // - See https://paperlessparts.atlassian.net/wiki/spaces/ENGINEERIN/pages/314277993/Viewer+Cube+Logic
  //   for details about faces, edges and corners
  // - About naming conventions: `p` prefix means positive, `n` prefix means negative
  //   So `pxpypz` means the corner at intersection of px, py, pz faces.
  // prettier-ignore
  const corners: UserData[] = [
    {
      name: 'pxpypz',
      position: pxpypzPosition,
      up: [
        //6 vector choices based on combinations of edges adjacent to corner projected on plane with normal=position
        calcUp(pxpyVector, pxpypzPosition),
        calcUp(pypxVector, pxpypzPosition),
        calcUp(pxpzVector, pxpypzPosition),
        calcUp(pzpxVector, pxpypzPosition),
        calcUp(pypzVector, pxpypzPosition),
        calcUp(pzpyVector, pxpypzPosition),
      ],
      coords: [1, 1, 1],
    },
    {
      name:'pxpynz',
      position: pxpynzPosition,
      up: [
        calcUp(pxpyVector, pxpynzPosition),
        calcUp(pypxVector, pxpynzPosition),
        calcUp(pxnzVector, pxpynzPosition),
        calcUp(nzpxVector, pxpynzPosition),
        calcUp(pynzVector, pxpynzPosition),
        calcUp(nzpyVector, pxpynzPosition),
      ],
      coords: [1, 1, -1]
    },
    {
      name:'pxnynz',
      position: pxnynzPosition,
      up: [
        //6 vector choices based on combinations of edges adjacent to corner projected on plane with normal=position
        calcUp(pxnyVector, pxnynzPosition),
        calcUp(nypxVector, pxnynzPosition),
        calcUp(pxnzVector, pxnynzPosition),
        calcUp(nzpxVector, pxnynzPosition),
        calcUp(nynzVector, pxnynzPosition),
        calcUp(nznyVector, pxnynzPosition),
      ],
      coords: [1, -1, -1]
    },
    {
      name:'pxnypz',
      position: pxnypzPosition,
      up: [
        calcUp(pxnyVector, pxnypzPosition),
        calcUp(nypxVector, pxnypzPosition),
        calcUp(pxpzVector, pxnypzPosition),
        calcUp(pzpxVector, pxnypzPosition),
        calcUp(nypzVector, pxnypzPosition),
        calcUp(pznyVector, pxnypzPosition),
      ],
      coords: [1, -1, 1]
    },
    {
      name:'nxnypz',
      position: nxnypzPosition,
      up: [
        //6 vector choices based on combinations of edges adjacent to corner projected on plane with normal=position
        calcUp(nxnyVector, nxnypzPosition),
        calcUp(nynxVector, nxnypzPosition),
        calcUp(nxpzVector, nxnypzPosition),
        calcUp(pznxVector, nxnypzPosition),
        calcUp(nypzVector, nxnypzPosition),
        calcUp(pznyVector, nxnypzPosition),
      ],
      coords: [-1, -1, 1]
    },
    {
      name:'nxnynz',
      position: nxnynzPosition,
      up: [
        //6 vector choices based on combinations of edges adjacent to corner projected on plane with normal=position
        calcUp(nxnyVector, nxnynzPosition),
        calcUp(nynxVector, nxnynzPosition),
        calcUp(nxnzVector, nxnynzPosition),
        calcUp(nznxVector, nxnynzPosition),
        calcUp(nynzVector, nxnynzPosition),
        calcUp(nznyVector, nxnynzPosition),
      ],
      coords: [-1, -1, -1]
    },
    {
      name:'nxpynz',
      position: nxpynzPosition,
      up: [
        calcUp(nxpyVector, nxpynzPosition),
        calcUp(pynxVector, nxpynzPosition),
        calcUp(nxnzVector, nxpynzPosition),
        calcUp(nznxVector, nxpynzPosition),
        calcUp(pynzVector, nxpynzPosition),
        calcUp(nzpyVector, nxpynzPosition),
      ],
      coords: [-1, 1, -1]
    },
    {
      name:'nxpypz',
      position: nxpypzPosition,
      up: [
        //6 vector choices based on combinations of edges adjacent to corner projected on plane with normal=position
        calcUp(nxpyVector, nxpypzPosition),
        calcUp(pynxVector, nxpypzPosition),
        calcUp(nxpzVector, nxpypzPosition),
        calcUp(pznxVector, nxpypzPosition),
        calcUp(pypzVector, nxpypzPosition),
        calcUp(pzpyVector, nxpypzPosition),
      ],
      coords: [-1, 1, 1]
    },
  ];

  // assertions for debugging during development: (currently no issues with the above definitions)
  // let cornerHasError = false;
  // for (const [index, corner] of corners.entries()) {
  //   const {position, up} = corner;
  //   for (const [upIndex, _up] of up.entries()) {
  //     if (Math.abs(position.dot(_up)) > 0.0001) {
  //       console.log(`corner ${index} = ${JSON.stringify(_up)} has up ${upIndex} that is not normal to position`);
  //       cornerHasError = true;
  //     }
  //   }
  // }
  // if (cornerHasError) {
  //   debugger;
  // }

  // prettier-ignore
  for (const corner of corners) {
    const v1x = corner.coords[0] * (l - e);
    const v1y = corner.coords[1] * l;
    const v1z = corner.coords[2] * l;
    const v1 = [v1x, v1y, v1z];

    const v2x = corner.coords[0] * (l - e);
    const v2y = corner.coords[1] * (l + t);
    const v2z = corner.coords[2] * (l + t);
    const v2 = [v2x, v2y, v2z];

    const v3x = corner.coords[0] * (l - e);
    const v3y = corner.coords[1] * l;
    const v3z = corner.coords[2] * (l - e);
    const v3 = [v3x, v3y, v3z];

    const v4x = corner.coords[0] * (l - e);
    const v4y = corner.coords[1] * (l + t);
    const v4z = corner.coords[2] * (l - e);
    const v4 = [v4x, v4y, v4z];

    const v5x = corner.coords[0] * (l - e);
    const v5y = corner.coords[1] * (l - e);
    const v5z = corner.coords[2] * l;
    const v5 = [v5x, v5y, v5z];

    const v6x = corner.coords[0] * (l - e);
    const v6y = corner.coords[1] * (l - e);
    const v6z = corner.coords[2] * (l + t);
    const v6 = [v6x, v6y, v6z];

    const v7x = corner.coords[0] * l;
    const v7y = corner.coords[1] * (l - e);
    const v7z = corner.coords[2] * l;
    const v7 = [v7x, v7y, v7z];

    const v8x = corner.coords[0] * (l + t);
    const v8y = corner.coords[1] * (l - e);
    const v8z = corner.coords[2] * (l + t);
    const v8 = [v8x, v8y, v8z];

    const v9x = corner.coords[0] * l;
    const v9y = corner.coords[1] * (l - e);
    const v9z = corner.coords[2] * (l - e);
    const v9 = [v9x, v9y, v9z];

    const v10x = corner.coords[0] * (l + t);
    const v10y = corner.coords[1] * (l - e);
    const v10z = corner.coords[2] * (l - e);
    const v10 = [v10x, v10y, v10z];

    const v11x = corner.coords[0] * l;
    const v11y = corner.coords[1] * l;
    const v11z = corner.coords[2] * (l - e);
    const v11 = [v11x, v11y, v11z];

    const v12x = corner.coords[0] * (l + t);
    const v12y = corner.coords[1] * (l + t);
    const v12z = corner.coords[2] * (l - e);
    const v12 = [v12x, v12y, v12z];

    const v13x = corner.coords[0] * (l + t);
    const v13y = corner.coords[1] * (l + t);
    const v13z = corner.coords[2] * (l + t);
    const v13 = [v13x, v13y, v13z];

    const reverse = corner.coords.filter(n => n !== 0).reduce((a,b) => a * b);

    let vert = [
      [v1, v3, v4, v4, v2, v1, v1, v2, v6, v6, v5, v1], // x corner edge
      [v7, v5, v6, v6, v8, v7, v7, v8, v10, v10, v9, v7], // y corner edge
      [v11, v9, v10, v10, v12, v11, v11, v12, v4, v4, v3, v11], // z corner edge
      [v13, v2, v4, v4, v12, v13], // x face
      [v13, v8, v6, v6, v2, v13], // y face
      [v13, v12, v10, v10, v8, v13], // z face
    ]

    if (reverse === 1) {
      vert = vert.map(vertFace => vertFace.reverse())
    }

    const geom = new BufferGeometry();
    geom.setAttribute(
      'position',
      new BufferAttribute(new Float32Array(vert.flat(2)), 3),
    );
    geom.computeVertexNormals();

    //corner
    const mesh = new Mesh(
      geom,
      new MeshStandardMaterial({
        color: COLOR_CODES.CUBE_HOVER_COLOR,
        depthWrite: false,
        transparent: true,
        opacity: 0
      }),
    ) as CubeMesh;

    mesh.userData.position = corner.position;
    mesh.userData.up = corner.up;
    group.add(mesh);
    selectables.push(mesh);
  }

  return { group, selectables };
};
